<script setup lang="ts">
import { useChatStore } from '@/stores/chatStore.ts'
import { computed, onMounted, watch } from 'vue'
import Backend from '@/models/backend.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import ChatParticipant from '@/components/Sections/Communication/ChatRoom/ChatParticipant.vue'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores
 */
const chatStore = useChatStore()

/**
 * Computed
 */
const chatParticipants = computed(() => {
  const roomMap = chatStore.roomParticipants.get(chatStore.activeRoomId)
  return roomMap ? [...roomMap.values()].sort((a, b) => a.id - b.id) : []
})

const currentRoom = computed(() => {
  return chatStore.getRoomById(chatStore.activeRoomId)
})

/**
 * Methods
 */
const loadParticipants = () => {
  if (chatStore.activeRoomId > 0) {
    if (!chatStore.getParticipantsInitialized(chatStore.activeRoomId)) {
      new Backend()
        .postRoomParticipants(chatStore.activeRoomId)
        .then(() => chatStore.setParticipantsInitialized(chatStore.activeRoomId))
        .catch((err) => logger.error('Error fetching participants:', err))
    }
  }
}

/**
 * Lifecycle
 */
onMounted(async () => {
  loadParticipants()
})

/**
 * Watchers
 */
// Watch for active room changes
watch(
  () => chatStore.activeRoomId,
  async () => {
    loadParticipants()
  }
)

// Watch for participantsMD5 changes in the current room
watch(
  () => currentRoom.value?.participantsMD5,
  async (newValue, oldValue) => {
    if (newValue !== oldValue && oldValue !== undefined) {
      // If MD5 changed and we're seeing this room, reload the participants
      loadParticipants()
    }
  }
)
</script>

<template>
  <div v-if="currentRoom" class="shrink-0 w-72 hidden md:block mr-2">
    <StandardCard :title="t('communication.chat.participants')" :no-padding="true">
      <div v-if="chatParticipants.length" class="flex flex-col justify-end min-h-full px-1 pt-2">
        <ChatParticipant
          v-for="(participant, index) in chatParticipants"
          :key="participant.id"
          :participant="participant"
          :room-type="currentRoom.roomType"
          :class="{ 'pt-2': index !== 0 }"
        />
      </div>
    </StandardCard>
  </div>
</template>

<style scoped lang="scss"></style>
